import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from "react-helmet";
import { AnimatePresence, useCycle, motion } from 'framer-motion';
import { LocomotiveScrollProvider, useLocomotiveScroll } from 'react-locomotive-scroll';
import 'locomotive-scroll/src/locomotive-scroll.scss';

// Components
import Header from '../../components/header';
import Footer from '../../components/footer';
import Modal from '../../components/modal';
import Accordion from '../../components/accordion';
import TagsList from '../../components/tagsList/tagsList';

// Utils
import { MoveWhenVisible } from '../../utils/moveWhenVisible';
import { FadeUpEachWhenVisible } from '../../utils/fadeUpEachWhenVisible';
import AnimatedCounter from '../../utils/animateCounter';

// Hooks
import useTeamPage from '../../hooks/ui/useTeamPage';
import useWindowSize from '../../hooks/utils/useWindowSize';
import useHandleTags from "../../hooks/utils/useHandleTags";
import useTeamMeta from "../../hooks/ui/useTeamMeta";

// Views
import FeaturedSection from './views/featuredSection';
import EmployeesList from './views/employeesList';
import FeaturedModal from './views/featuredModal';

function TeamNew() {
    const containerRef = useRef(null);
    
    const { activeChips, handleChips } = useHandleTags();
    const [employee, setEmployee] = useState(null);
    const [index, setIndex] = useState(0);

    const [isVisible, onCycle] = useCycle(true, false);
    const [isVisibleModalFeatured, onIsVisibleModalFeatured] = useCycle(true, false);
    const { scroll } = useLocomotiveScroll();
    const { width } = useWindowSize();

    const isMobile = width <= 480;

    const {
        title,
        percentBlockSection,
        showFeaturedEmployee,
        featuredEmployeeSection,
        tipsListSection,
        employeesListSection
    } = useTeamPage();
    
    const { oggDescription, oggTitle, oggImage} = useTeamMeta();
    
    useEffect(() => {
        if (!isVisible && scroll) {
            scroll.stop();
        }
        if (scroll && isVisible) {
            scroll && scroll.start();
        }
    }, [isVisible, scroll]);

    const setEmployeeData = (data, index, onCycle) => {
        setEmployee(data);
        setIndex(index);
        onCycle(onCycle);
    };
    
    const filteredUser = activeChips.length
        ? employeesListSection.filter((user) =>
              user.fields.tagsList.length === activeChips.length
                  ? user.fields.tagsList.every((tag) =>
                        activeChips.map((chip) => chip).includes(tag.fields.title)
                    )
                  : activeChips
                        .map((chip) => chip)
                        .every((chip) =>
                            user.fields.tagsList.map((t) => t.fields.title).includes(chip)
                        )
          )
        : employeesListSection;
    
    return (
        <LocomotiveScrollProvider
            options={{
                smooth: true,
                scrollFromAnywhere: true,
                multiplier: 0.75,
                lerp: 0.03,
                tablet: { smooth: true, multiplier: 0.55 },
                smartphone: { smooth: false }
            }}
            watch={[]}
            containerRef={containerRef}>
            <Helmet>
                <title>{oggTitle}</title>
                <meta property="og:title" content={oggTitle} />
                <meta property="og:description" content={oggDescription} />
                <meta property="og:image" content={oggImage.url} />
            </Helmet>
            <div className={'page team-page'} data-scroll-container ref={containerRef}>
                <Header />

                <div className={'page_view'} data-scroll-section>
                    <div className={`team-about flex ${showFeaturedEmployee && 'line'}`} data-scroll>
                        <MoveWhenVisible fromX={0} toX={20} delayFrom={0.2} delayTo={0.2}>
                            <p className={'page_title new-news_title'} id="team">
                                {title}
                            </p>
                        </MoveWhenVisible>
                        <ul className={'numbers'}>
                            {percentBlockSection.map((num, index) => {
                                const numberSting = num.fields.percentage.slice(0, 2);
                                const number = parseInt(numberSting, 10);
                                return (
                                    <FadeUpEachWhenVisible
                                        index={index}
                                        toY={0}
                                        fromY={20}
                                        delayTo={0.5}
                                        delayFrom={0.5}
                                        duration={0.8}
                                        key={index}>
                                        <li>
                                            <div className={'percent'}>
                                                <AnimatedCounter from={0} to={number} />
                                                {num.fields.specialCharacter}
                                            </div>
                                            <p>{num.fields.title}</p>
                                        </li>
                                    </FadeUpEachWhenVisible>
                                );
                            })}
                        </ul>
                    </div>

                    {showFeaturedEmployee && (
                        <FeaturedSection
                            featuredEmployeeSection={featuredEmployeeSection}
                            onIsVisibleModalFeatured={onIsVisibleModalFeatured}
                        />
                    )}

                    <Accordion isMobile={isMobile} hideTeamCloseBtn={showFeaturedEmployee}>
                        <TagsList classNames={'team-tips'} tagsList={tipsListSection} employeesListSection={employeesListSection} activeChips={activeChips} handleChips={handleChips} />
                    </Accordion>

                    <div className={'team-result'}>

                        { tipsListSection.length ?
                          <AnimatePresence>
                            {!filteredUser.length ? (
                              <motion.div
                                className={'no-matches'}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}>
                                No matches found, please limit your selections.
                              </motion.div>
                            ) : (
                              ''
                            )}
                          </AnimatePresence>
                          :
                          null
                        }

                        <EmployeesList
                            activeChips={activeChips}
                            employees={filteredUser}
                            setEmployeeData={setEmployeeData}
                            onCycle={onCycle}
                        />
                    </div>
                </div>

                <AnimatePresence>
                    {!isVisible && (
                        <Modal
                            activeChips={activeChips}
                            onCycle={onCycle}
                            employee={employee}
                            index={index}
                            link={'/team'}
                            isVisible={isVisible}
                        />
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {!isVisibleModalFeatured && (
                        <FeaturedModal
                            link={'/team'}
                            featuredEmployeeSection={featuredEmployeeSection}
                            onIsVisibleModalFeatured={onIsVisibleModalFeatured}
                            isVisibleModalFeatured={isVisibleModalFeatured}
                        />
                    )}
                </AnimatePresence>
                <Footer />
            </div>
        </LocomotiveScrollProvider>
    );
}

export default TeamNew;
